import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import CommentIcon from '@mui/icons-material/Comment';
import { useTranslation } from 'react-i18next';
import { getShooters, getShooter, updateShooterComments } from '../../api';
import Snackbar from '../../components/Snackbar';
import ShooterInfo from '../../components/ShooterInfo';

export default function Comments() {
  const { matchId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [shooters, setShooters] = useState([]);
  const [selectedShooter, setSelectedShooter] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const loadShooters = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getShooters(matchId);
      const list = Array.isArray(data) ? data : [];
      setShooters(list);

      // If shooterId in URL, pre-select
      const shooterId = searchParams.get('shooterId');
      if (shooterId) {
        const found = list.find((s) => s.id === shooterId);
        if (found) {
          setSelectedShooter(found);
          setComments(found.comments || []);
        }
      }
    } catch (err) {
      setSnack({ open: true, message: 'Failed to load shooters', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [matchId, searchParams]);

  useEffect(() => { loadShooters(); }, [loadShooters]);

  const handleSelectShooter = async (shooter) => {
    if (!shooter) {
      setSelectedShooter(null);
      setComments([]);
      return;
    }
    try {
      const { data } = await getShooter(matchId, shooter.id);
      setSelectedShooter(data);
      setComments(data.comments || []);
    } catch {
      setSelectedShooter(shooter);
      setComments(shooter.comments || []);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedShooter) return;
    const updatedComments = [
      ...comments,
      {
        text: newComment.trim(),
        timestamp: Date.now(),
        author: sessionStorage.getItem('auth') || 'staff',
      },
    ];
    setSaving(true);
    try {
      await updateShooterComments(matchId, selectedShooter.id, { comments: updatedComments });
      setComments(updatedComments);
      setNewComment('');
      setSnack({ open: true, message: 'Comment added', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || 'Failed to add comment', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteComment = async (idx) => {
    const updatedComments = comments.filter((_, i) => i !== idx);
    setSaving(true);
    try {
      await updateShooterComments(matchId, selectedShooter.id, { comments: updatedComments });
      setComments(updatedComments);
      setSnack({ open: true, message: 'Comment deleted', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: 'Failed to delete comment', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        <CommentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {t('registration.comments')}
      </Typography>

      <Autocomplete
        options={shooters}
        getOptionLabel={(s) => `${s.publicId ? `#${s.publicId} ` : ''}${s.name}`}
        value={selectedShooter}
        onChange={(_, v) => handleSelectShooter(v)}
        renderInput={(params) => <TextField {...params} label="Select Shooter" />}
        sx={{ mb: 3 }}
        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
      />

      {selectedShooter && (
        <>
          <ShooterInfo shooter={selectedShooter} showDetails />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>Comments ({comments.length})</Typography>

          {comments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No comments yet</Typography>
          ) : (
            <List>
              {comments.map((comment, idx) => (
                <ListItem
                  key={idx}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleDeleteComment(idx)} disabled={saving}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={comment.text}
                    secondary={`${comment.author || 'Staff'} - ${comment.timestamp ? new Date(comment.timestamp).toLocaleString() : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <TextField
              label="New Comment"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              fullWidth
              multiline
              rows={2}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAddComment();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddComment}
              disabled={saving || !newComment.trim()}
              sx={{ alignSelf: 'flex-end' }}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            >
              Add
            </Button>
          </Box>
        </>
      )}

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} />
    </Container>
  );
}
